import { Dialog, Grid, Typography, Switch } from '@material-ui/core';
import { Hidden } from '@mui/material';
import { VFC, useState } from 'react';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import { useHooks } from './useHooks';
import { useStyles } from './useStyles';
import { SiteProcessTextfield } from '../../input/ProcessTextfield';
import { SiteProcessSelectfield } from '../../input/ProcessSelectfield';
import { SiteProcessDatePicker } from '../../input/ProcessDatePicker';
import { ModalSubmitButton } from '../../cloudStorage/style/style';
import { CustomTooltip } from '../../../tooltip/CustomTooltip';
import { tooltipContents } from '../../../tooltip/Const';
import HelpIcon from '@material-ui/icons/Help';
import {
  TAddDailyReportForWorkersForm,
  TConstructForWorkers
} from '../../../../types/dailyReport';
import { DailyReportImageListItem } from '../DailyReportImageListItem';
import { TSelectItem } from '../../../../types/common';
import { MAX_IMAGE_COUNT } from '../../../../const/DailyReportConst';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  acceptablePictureContentTypes,
  acceptablePictureTypeStr
} from '../../../../utils/common';

export type Props = {
  construct: TConstructForWorkers;
  siteProcessList: TSelectItem[];
  showMessage: (message: string) => void;
};

export const DailyReportForWorkersView: VFC<Props> = props => {
  const { construct, siteProcessList, showMessage } = props;
  const classes = useStyles();

  const [photoSyncEnabled, setPhotoSyncEnabled] = useState(false);

  const methods: UseFormReturn<TAddDailyReportForWorkersForm> = useForm();
  const {
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  const {
    submit,
    files,
    deletePicture,
    getRootProps,
    getInputProps,
    handleCaptureImage
  } = useHooks({
    reset,
    constructId: construct.id,
    showMessage,
    photoSyncEnabled
  });

  const theme = useTheme();
  const isSmDownScreen = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <Dialog
      aria-labelledby='transition-modal-title'
      aria-describedby='transition-modal-description'
      className={classes.modal}
      open={true}
      maxWidth={'sm'}
      fullWidth={true}
    >
      <Grid container>
        <Grid item xs={12} style={{ paddingTop: 36, paddingBottom: 36 }}>
          <h1 className={classes.modalTitle}>日報作成</h1>
          <Grid container alignItems='center' justify='center'>
            <Grid container xs={10} alignItems='center' justify='center'>
              <FormProvider {...methods}>
                {/* 案件名 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>案件名</label>
                </Grid>
                <Grid item xs={10} md={10} className={classes.gridItem}>
                  {construct.name}
                </Grid>
                {/* /案件名 */}
                {/* 作業日 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>作業日*</label>
                </Grid>
                <Grid item xs={10} md={10}>
                  <div className={classes.leftAlignContainer}>
                    <Grid container>
                      <Grid item xs={8} md={5}>
                        <div className={classes.itemMargin}>
                          <SiteProcessDatePicker
                            name='work_day'
                            label='作業日'
                            required={true}
                            fullWidth={true}
                            errorMessage={errors?.work_day?.message}
                            className={classes.gridItem}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
                {/* /作業日 */}
                {/* 社名 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>社名*</label>
                </Grid>
                <Grid item xs={10} md={10}>
                  <SiteProcessTextfield
                    name='company_name'
                    className={`${classes.input} ${classes.gridItem}`}
                    required={true}
                    maxLength={50}
                    errorMessage={errors?.company_name?.message}
                  />
                </Grid>
                {/* /社名 */}
                {/* 氏名 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>氏名*</label>
                </Grid>
                <Grid item xs={10} md={10}>
                  <SiteProcessTextfield
                    name='worker_name'
                    className={`${classes.input} ${classes.gridItem}`}
                    required={true}
                    maxLength={50}
                    errorMessage={errors?.worker_name?.message}
                  />
                </Grid>
                {/* /氏名 */}
                {/* 作業内容 */}
                <Grid container alignItems='center' justify='center'>
                  <Grid item xs={10} md={2} className={classes.gridItem}>
                    <label className={classes.label}>作業内容</label>
                  </Grid>
                  <Grid item xs={10} md={10}>
                    <SiteProcessSelectfield
                      name='site_process_id'
                      listDatas={siteProcessList}
                      className={`${classes.input} ${classes.gridItem}`}
                      defaultValue={{ value: 0, label: '--' }}
                    />
                  </Grid>
                </Grid>
                {/* /作業内容 */}
                {/* /報告 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>報告*</label>
                </Grid>
                <Grid item xs={10} md={10}>
                  <SiteProcessTextfield
                    name='report_detail'
                    required={true}
                    fullWidth={true}
                    multiline={true}
                    style={{ padding: 0 }}
                    maxLength={1000}
                    className={`${classes.input} ${classes.gridItem}`}
                    errorMessage={errors?.report_detail?.message}
                    inputProps={{ minHeight: 60, maxHeight: 100 }}
                    inputElementProps={{ style: { padding: '2px 14px' } }}
                  />
                </Grid>
                {/* /報告 */}
                {/* 写真 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>写真</label>
                </Grid>
                {/* レスポンシブ */}
                {!isSmDownScreen && (
                  <Grid
                    container
                    xs={12}
                    spacing={1}
                    alignItems='center'
                    justify='center'
                  >
                    <Grid
                      item
                      xs={5}
                      className={`${classes.referFileButtonWrapperRwd}`}
                    >
                      <input
                        accept='image/*'
                        id='fileButton'
                        type='file'
                        style={{ display: 'none' }}
                        onChange={handleCaptureImage}
                        multiple={true}
                      />
                      <label
                        htmlFor='fileButton'
                        className={classes.referFileButtonRwd}
                      >
                        ファイルを参照
                      </label>
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      className={`${classes.referFileButtonWrapperRwd}`}
                    >
                      <input
                        accept='image/*'
                        capture='environment'
                        id='cameraButton'
                        type='file'
                        style={{ display: 'none' }}
                        onChange={handleCaptureImage}
                      />
                      <label
                        htmlFor='cameraButton'
                        className={classes.referFileButtonRwd}
                      >
                        カメラ撮影
                      </label>
                    </Grid>
                    <Grid item xs={10}>
                      <Typography
                        variant='body2'
                        align='center'
                        gutterBottom={true}
                      >
                        ※保存可能なファイル形式：
                        <br />
                        {acceptablePictureTypeStr()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={10} md={10} className={classes.gridItem}>
                  {/* 標準レイアウト */}
                  {isSmDownScreen && (
                    <div className={classes.dropArea} {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Typography
                        variant='body1'
                        align='center'
                        gutterBottom={true}
                      >
                        画像をドラッグ&ドロップしてください。
                      </Typography>
                      <Grid item className={classes.referFileButtonWrapper}>
                        <ModalSubmitButton
                          variant='contained'
                          onClick={() => {}}
                        >
                          ファイルを参照
                        </ModalSubmitButton>
                      </Grid>
                      <Typography
                        variant='body2'
                        align='center'
                        gutterBottom={true}
                      >
                        または、ファイル参照より画像を選択してください。
                      </Typography>
                      <Typography
                        variant='body2'
                        align='center'
                        gutterBottom={true}
                      >
                        ※保存可能なファイル形式：{acceptablePictureTypeStr()}
                      </Typography>
                    </div>
                  )}
                  <Hidden xsDown></Hidden>
                  {files.length > 0 && (
                    <>
                      <div className={classes.imageListContainer}>
                        {files.map((file: File, index: number) => (
                          <DailyReportImageListItem
                            file={file}
                            index={index}
                            deletePicture={deletePicture}
                          />
                        ))}
                      </div>
                      <Typography variant='subtitle2' align='right'>
                        {`選択枚数 ${files.length}/${MAX_IMAGE_COUNT}`}
                      </Typography>
                    </>
                  )}
                </Grid>
                {/* /写真 */}
                {/* ストレージ自動連携 */}
                <Grid item xs={10} md={2} className={classes.gridItem}>
                  <label className={classes.label}>
                    ストレージ
                    <br />
                    自動連携
                  </label>
                </Grid>
                <Grid item xs={10} md={10} className={classes.gridItem}>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>OFF</Grid>
                    <Grid item>
                      <Switch
                        checked={photoSyncEnabled}
                        onChange={e => {
                          setPhotoSyncEnabled(e.target.checked);
                        }}
                        color='primary'
                        name='photoSyncEnabled'
                        inputProps={{ 'aria-label': 'storage sync toggle' }}
                      />
                    </Grid>
                    <Grid item>ON</Grid>
                    <Grid item>
                      <CustomTooltip
                        title={tooltipContents.PhotoSyncEnabledButtonForWokers}
                      >
                        <HelpIcon color='action' />
                      </CustomTooltip>
                    </Grid>
                  </Grid>
                </Grid>
                {/* ストレージ自動連携 */}
              </FormProvider>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.modalFooter}>
          {/* 実行ボタン */}
          <ModalSubmitButton
            className={classes.button}
            variant='contained'
            onClick={handleSubmit(submit, (erros: any) => {})}
          >
            報告する
          </ModalSubmitButton>
          {/* /実行ボタン */}
        </Grid>
      </Grid>
    </Dialog>
  );
};
